/**
 * Dice recognition service
 * Calls the backend REST API for dice recognition
 */

import { cognitoService } from './cognitoService';
import { createScopedReporter } from './errorReporter';
import { useDiceRegistryStore } from '../stores/diceRegistryStore';
import { API_BASE_URL, API_KEY } from '@/config/api';

const SERVICE_TAG = '[RECOGNITION]';
const reporter = createScopedReporter('RECOGNITION');

/**
 * Strip the data URL prefix from a base64 string if present.
 *
 * Camera capture (canvas.toDataURL()) produces "data:image/jpeg;base64,/9j/..."
 * but the backend expects only the raw base64 payload.
 *
 * @param input - Raw base64 or data URL with base64 payload
 * @returns Raw base64 string without any prefix
 */
export function stripDataUrlPrefix(input: string): string {
  const commaIndex = input.indexOf(',');
  if (commaIndex === -1) {
    return input;
  }

  const prefix = input.substring(0, commaIndex);
  if (prefix.startsWith('data:')) {
    return input.substring(commaIndex + 1);
  }

  return input;
}

export interface RecognitionResult {
  value: number;
  confidence: number;
  provider: string;
  visibleFaces?: number[];
  reasoning?: string;
  /** Inferred dice type (when using auto-detect) */
  diceType?: string;
  /** Processing mode used */
  mode?: 'sequential' | 'consensus';
  /** Consensus data (only present when useConsensus is enabled) */
  consensus?: {
    consensusType: 'unanimous' | 'majority' | 'arbitrated' | 'failed';
    warnings: string[];
    providers: {
      attempted: string[];
      succeeded: string[];
    };
    usedArbitration: boolean;
  };
}

/**
 * Specification for expected dice in multi-dice recognition
 */
export interface DiceSpec {
  type: string;
  count: number;
}

/**
 * Individual die detected in multi-dice recognition
 */
export interface DetectedDie {
  value: number;
  confidence: number;
  provider: string;
  /** Inferred or specified dice type */
  diceType?: string;
  /** Bounding box of detected die in image */
  boundingBox?: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
  /** Consensus data if consensus mode was used */
  consensus?: {
    consensusType: 'unanimous' | 'majority' | 'arbitrated' | 'failed';
    warnings: string[];
  };
}

/**
 * Result from multi-dice recognition
 */
export interface MultiDiceResult {
  mode: 'multi-dice';
  /** Number of dice expected (from expectedDice param) */
  expected: number;
  /** Number of dice detected in image */
  detected: number;
  /** Number of dice successfully recognized */
  recognized: number;
  /** Individual dice results */
  dice: DetectedDie[];
  /** Processing time in milliseconds */
  processingTimeMs: number;
  /** Segmentation metadata */
  segmentation: {
    diceCount: number;
    labelsFound: string[];
  };
  /** Warnings for partial detection or other issues */
  warnings?: string[];
}

/**
 * Options for dice recognition
 */
export interface RecognitionOptions {
  /** Additional frames for multi-frame capture */
  frames?: string[];
  /** Enable consensus mode (default: true) */
  useConsensus?: boolean;
  /** AI Judge model to use for recognition (arbitration/tie-break) */
  model?: 'gpt-4.1-mini' | 'gpt-5.2' | 'claude-opus-4-7';
}

/**
 * Custom error class for recognition failures with detailed info
 */
export class RecognitionError extends Error {
  public readonly code: string;
  public readonly details: string;
  public readonly apiUrl?: string;

  constructor(code: string, message: string, details: string, apiUrl?: string) {
    super(message);
    this.name = 'RecognitionError';
    this.code = code;
    this.details = details;
    this.apiUrl = apiUrl;
  }

  /**
   * Get user-friendly error message (for production)
   */
  getUserMessage(): string {
    return this.message;
  }

  /**
   * Get detailed error message (for development)
   */
  getDetailedMessage(): string {
    const parts = [this.message];
    if (this.details) parts.push(`Details: ${this.details}`);
    if (this.apiUrl) parts.push(`API: ${this.apiUrl}`);
    parts.push(`Code: ${this.code}`);
    return parts.join('\n');
  }
}

interface RecognitionConfig {
  apiUrl: string;
  apiKey: string;
}

// Configuration - can be set via environment or at runtime
let config: RecognitionConfig = {
  apiUrl: API_BASE_URL,
  apiKey: API_KEY
};

/**
 * Configure the recognition service
 */
export function configureRecognition(newConfig: Partial<RecognitionConfig>): void {
  config = { ...config, ...newConfig };
  console.info(`${SERVICE_TAG} Configured`, { apiUrl: config.apiUrl });
}

/**
 * Recognize dice value from image
 * Calls backend API - throws RecognitionError on failure
 *
 * @param imageData - Base64 encoded image data
 * @param diceType - Type of dice (d4, d6, d8, d10, d12, d20, d100)
 * @param options - Optional recognition options (frames, useConsensus)
 * @returns Recognition result with value, confidence, and consensus data
 */
export async function recognizeDice(
  imageData: string,
  diceType: string,
  options: RecognitionOptions = {}
): Promise<RecognitionResult> {
  const apiUrl = `${config.apiUrl}/recognize`;

  // Check if API is configured
  if (!config.apiUrl) {
    reporter.error('Recognition service not configured', {
      technicalDetails: 'VITE_API_URL environment variable is not set',
      showToast: false // Pages handle error display
    });
    throw new RecognitionError(
      'CONFIG_MISSING_URL',
      'Recognition service not configured',
      'VITE_API_URL environment variable is not set',
      'Not configured'
    );
  }

  if (!config.apiKey) {
    reporter.error('Recognition service not configured', {
      technicalDetails: 'VITE_API_KEY environment variable is not set',
      showToast: false // Pages handle error display
    });
    throw new RecognitionError(
      'CONFIG_MISSING_KEY',
      'Recognition service not configured',
      'VITE_API_KEY environment variable is not set',
      apiUrl
    );
  }

  try {
    const { frames, useConsensus, model } = options;
    const deviceId = useDiceRegistryStore.getState().deviceId;

    console.info(`${SERVICE_TAG} Calling recognition API`, {
      diceType,
      imageLength: imageData.length,
      frameCount: frames?.length ?? 0,
      useConsensus: useConsensus ?? true,
      model: model ?? 'default',
      apiUrl
    });

    // Strip data URL prefix — backend expects raw base64
    const rawBase64 = stripDataUrlPrefix(imageData);

    // Build request body with optional fields
    const requestBody: Record<string, unknown> = {
      image: rawBase64,
      diceType,
      ...(deviceId ? { deviceId } : {})
    };

    // Add frames if provided, stripping data URL prefixes from each
    if (frames && frames.length > 0) {
      requestBody.frames = frames.map(stripDataUrlPrefix);
    }

    // Add options if any are specified
    const requestOptions: Record<string, unknown> = {};
    if (useConsensus !== undefined) {
      requestOptions.useConsensus = useConsensus;
    }
    if (model) {
      requestOptions.model = model;
    }
    if (Object.keys(requestOptions).length > 0) {
      requestBody.options = requestOptions;
    }

    const requestHeaders: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Api-Key': config.apiKey
    };
    const idToken = await cognitoService.getIdToken();
    if (idToken) {
      requestHeaders['Authorization'] = `Bearer ${idToken}`;
    }

    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: requestHeaders,
      body: JSON.stringify(requestBody)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({})) as { message?: string };
      reporter.error('Recognition failed', {
        technicalDetails: `API returned ${response.status}: ${response.statusText}`,
        context: { status: response.status, error: errorData },
        showToast: false // Pages handle error display
      });

      // Specific error for CORS issues
      if (response.status === 0) {
        throw new RecognitionError(
          'CORS_ERROR',
          'Unable to connect to recognition server',
          'CORS policy blocked the request. The server may not allow requests from this domain.',
          apiUrl
        );
      }

      throw new RecognitionError(
        `HTTP_${response.status}`,
        errorData.message || `Server error (${response.status})`,
        `Status: ${response.status} ${response.statusText}`,
        apiUrl
      );
    }

    const result = await response.json() as RecognitionResult;

    console.info(`${SERVICE_TAG} Recognition successful`, {
      diceType,
      value: result.value,
      confidence: Math.round(result.confidence * 100),
      provider: result.provider
    });

    return result;
  } catch (error) {
    // Re-throw RecognitionError as-is
    if (error instanceof RecognitionError) {
      throw error;
    }

    // Handle network errors (fetch failures)
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    reporter.error('Recognition failed', {
      originalError: error instanceof Error ? error : undefined,
      context: { diceType },
      showToast: false // Pages handle error display
    });

    // Check for common network error patterns
    if (errorMessage.includes('Failed to fetch') || errorMessage.includes('NetworkError')) {
      throw new RecognitionError(
        'NETWORK_ERROR',
        'Unable to connect to recognition server',
        `Network error: ${errorMessage}. Check your internet connection or CORS settings.`,
        apiUrl
      );
    }

    if (errorMessage.includes('CORS') || errorMessage.includes('cross-origin')) {
      throw new RecognitionError(
        'CORS_ERROR',
        'Unable to connect to recognition server',
        `CORS error: ${errorMessage}. The server needs to allow requests from this domain.`,
        apiUrl
      );
    }

    throw new RecognitionError(
      'UNKNOWN_ERROR',
      'Recognition failed',
      errorMessage,
      apiUrl
    );
  }
}

/**
 * Get display name for dice type
 */
export function getDiceDisplayName(diceType: string): string {
  return diceType.toUpperCase();
}

/**
 * Available dice types
 */
export const DICE_TYPES = [
  { value: 'd4', label: 'D4', max: 4 },
  { value: 'd6', label: 'D6', max: 6 },
  { value: 'd8', label: 'D8', max: 8 },
  { value: 'd10', label: 'D10', max: 10 },
  { value: 'd12', label: 'D12', max: 12 },
  { value: 'd20', label: 'D20', max: 20 }
] as const;

/**
 * Get max value for a dice type
 *
 * @param diceType - The dice type (e.g., 'd6', 'd20')
 * @returns The maximum value for that dice type
 */
export function getDiceMax(diceType: string): number {
  const die = DICE_TYPES.find((d) => d.value === diceType.toLowerCase());
  if (die) {
    return die.max;
  }
  // Handle d100 and unknown types
  if (diceType.toLowerCase() === 'd100') {
    return 100;
  }
  reporter.warning('Unknown dice type, using default max', {
    context: { diceType, fallbackMax: 20 },
    showToast: false
  });
  return 20;
}

/**
 * Recognize dice with auto-detection (no dice type specified)
 * Uses backend's multi-dice segmentation to detect and identify dice type.
 *
 * @param imageData - Base64 encoded image data
 * @param options - Optional recognition options
 * @returns Recognition result with inferred dice type
 */
export async function recognizeAuto(
  imageData: string,
  options: RecognitionOptions = {}
): Promise<RecognitionResult> {
  const apiUrl = `${config.apiUrl}/recognize`;

  validateConfig(apiUrl);

  try {
    const { useConsensus, model } = options;
    const deviceId = useDiceRegistryStore.getState().deviceId;

    console.info(`${SERVICE_TAG} Calling auto-detect recognition API`, {
      imageLength: imageData.length,
      useConsensus: useConsensus ?? true,
      model: model ?? 'default',
      apiUrl
    });

    // Strip data URL prefix — backend expects raw base64
    const rawBase64 = stripDataUrlPrefix(imageData);

    // No expectedDice and no diceType — triggers backend auto-detect mode
    // Backend will use segmentation + AI to infer the dice type
    const requestBody: Record<string, unknown> = {
      image: rawBase64,
      ...(deviceId ? { deviceId } : {})
    };

    // Add options if any are specified
    const requestOptions: Record<string, unknown> = {};
    if (useConsensus !== undefined) {
      requestOptions.useConsensus = useConsensus;
    }
    if (model) {
      requestOptions.model = model;
    }
    if (Object.keys(requestOptions).length > 0) {
      requestBody.options = requestOptions;
    }

    const autoHeaders: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Api-Key': config.apiKey
    };
    const autoToken = await cognitoService.getIdToken();
    if (autoToken) {
      autoHeaders['Authorization'] = `Bearer ${autoToken}`;
    }

    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: autoHeaders,
      body: JSON.stringify(requestBody)
    });

    if (!response.ok) {
      await handleApiError(response, apiUrl);
    }

    // Auto-detect backend returns a flat single-die response:
    // { value, confidence, provider, diceType, typeDetection, ... }
    const result = (await response.json()) as {
      value: number;
      confidence: number;
      provider: string;
      diceType?: string;
      typeDetection?: { method: string; detectedType: string; confidence: number };
      consensus?: Record<string, unknown>;
    };

    if (result.value === undefined || result.value === null) {
      throw new RecognitionError(
        'NO_DICE_DETECTED',
        'No dice detected in image',
        'The recognition system could not find any dice in the captured image.',
        apiUrl
      );
    }

    console.info(`${SERVICE_TAG} Auto-detect recognition successful`, {
      value: result.value,
      confidence: Math.round(result.confidence * 100),
      provider: result.provider,
      diceType: result.diceType ?? 'unknown',
      typeDetection: result.typeDetection
    });

    return {
      value: result.value,
      confidence: result.confidence,
      provider: result.provider,
      diceType: result.diceType,
      consensus: undefined
    };
  } catch (error) {
    return handleRecognitionError(error, 'auto-detect', apiUrl);
  }
}

/**
 * Recognize multiple dice in a single image
 *
 * @param imageData - Base64 encoded image data
 * @param expectedDice - Optional expected dice specifications
 * @param options - Optional recognition options
 * @returns Multi-dice result with all detected dice
 */
export async function recognizeMultipleDice(
  imageData: string,
  expectedDice?: DiceSpec[],
  options: RecognitionOptions = {}
): Promise<MultiDiceResult> {
  const apiUrl = `${config.apiUrl}/recognize`;

  validateConfig(apiUrl);

  try {
    const { useConsensus, model } = options;
    const totalExpected = expectedDice?.reduce((sum, d) => sum + d.count, 0) ?? 0;
    const deviceId = useDiceRegistryStore.getState().deviceId;

    console.info(`${SERVICE_TAG} Calling multi-dice recognition API`, {
      imageLength: imageData.length,
      expectedDice: expectedDice?.map((d) => `${d.count}${d.type}`),
      totalExpected,
      useConsensus: useConsensus ?? true,
      model: model ?? 'default',
      apiUrl
    });

    // Strip data URL prefix — backend expects raw base64
    const rawBase64 = stripDataUrlPrefix(imageData);

    const requestBody: Record<string, unknown> = {
      image: rawBase64,
      ...(deviceId ? { deviceId } : {})
    };

    if (expectedDice && expectedDice.length > 0) {
      requestBody.expectedDice = expectedDice;
    }

    // Add options if any are specified
    const requestOptions: Record<string, unknown> = {};
    if (useConsensus !== undefined) {
      requestOptions.useConsensus = useConsensus;
    }
    if (model) {
      requestOptions.model = model;
    }
    if (Object.keys(requestOptions).length > 0) {
      requestBody.options = requestOptions;
    }

    const multiHeaders: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Api-Key': config.apiKey
    };
    const multiToken = await cognitoService.getIdToken();
    if (multiToken) {
      multiHeaders['Authorization'] = `Bearer ${multiToken}`;
    }

    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: multiHeaders,
      body: JSON.stringify(requestBody)
    });

    if (!response.ok) {
      await handleApiError(response, apiUrl);
    }

    const result = (await response.json()) as MultiDiceResult;

    console.info(`${SERVICE_TAG} Multi-dice recognition successful`, {
      expected: result.expected,
      detected: result.detected,
      recognized: result.recognized,
      processingTimeMs: result.processingTimeMs
    });

    return result;
  } catch (error) {
    if (error instanceof RecognitionError) {
      throw error;
    }
    return handleMultiDiceError(error, apiUrl);
  }
}

/**
 * Validate API configuration
 */
function validateConfig(apiUrl: string): void {
  if (!config.apiUrl) {
    reporter.error('Recognition service not configured', {
      technicalDetails: 'VITE_API_URL environment variable is not set',
      showToast: false
    });
    throw new RecognitionError(
      'CONFIG_MISSING_URL',
      'Recognition service not configured',
      'VITE_API_URL environment variable is not set',
      'Not configured'
    );
  }

  if (!config.apiKey) {
    reporter.error('Recognition service not configured', {
      technicalDetails: 'VITE_API_KEY environment variable is not set',
      showToast: false
    });
    throw new RecognitionError(
      'CONFIG_MISSING_KEY',
      'Recognition service not configured',
      'VITE_API_KEY environment variable is not set',
      apiUrl
    );
  }
}

/**
 * Handle API error responses
 */
async function handleApiError(response: Response, apiUrl: string): Promise<never> {
  const errorData = (await response.json().catch(() => ({}))) as { message?: string };
  reporter.error('Recognition API error', {
    technicalDetails: `Status ${response.status}: ${response.statusText}`,
    context: { status: response.status },
    showToast: false
  });

  if (response.status === 0) {
    throw new RecognitionError(
      'CORS_ERROR',
      'Unable to connect to recognition server',
      'CORS policy blocked the request.',
      apiUrl
    );
  }

  throw new RecognitionError(
    `HTTP_${response.status}`,
    errorData.message || `Server error (${response.status})`,
    `Status: ${response.status} ${response.statusText}`,
    apiUrl
  );
}

/**
 * Handle recognition errors with standard error mapping
 */
function handleRecognitionError(
  error: unknown,
  context: string,
  apiUrl: string
): never {
  if (error instanceof RecognitionError) {
    throw error;
  }

  const errorMessage = error instanceof Error ? error.message : 'Unknown error';
  reporter.error('Recognition failed', {
    originalError: error instanceof Error ? error : undefined,
    context: { recognitionContext: context },
    showToast: false
  });

  if (errorMessage.includes('Failed to fetch') || errorMessage.includes('NetworkError')) {
    throw new RecognitionError(
      'NETWORK_ERROR',
      'Unable to connect to recognition server',
      `Network error: ${errorMessage}`,
      apiUrl
    );
  }

  if (errorMessage.includes('CORS') || errorMessage.includes('cross-origin')) {
    throw new RecognitionError(
      'CORS_ERROR',
      'Unable to connect to recognition server',
      `CORS error: ${errorMessage}`,
      apiUrl
    );
  }

  throw new RecognitionError('UNKNOWN_ERROR', 'Recognition failed', errorMessage, apiUrl);
}

/**
 * Handle multi-dice recognition errors
 */
function handleMultiDiceError(error: unknown, apiUrl: string): never {
  const errorMessage = error instanceof Error ? error.message : 'Unknown error';
  reporter.error('Multi-dice recognition failed', {
    originalError: error instanceof Error ? error : undefined,
    showToast: false
  });

  throw new RecognitionError(
    'MULTI_DICE_ERROR',
    'Multi-dice recognition failed',
    errorMessage,
    apiUrl
  );
}
